import * as React from 'react';
import { ProductSheetEquipmentDetails } from '../../../../../../config/order-tracker-new-common/constants';
import { ProductDetailsSection } from '../product-details-section';
import StaticData, { StaticDataGroup } from 'ted-ui/build/StaticData';
import Spacing from '@able/react/dist/Spacing';
import ActionButton from '@able/react/dist/ActionButton';
import { IEquipmentDetailsProps } from '@applications/order-tracker/config/order-tracker-new-common/types';
import { productEquipmentDetailsBusinessRules } from '@applications/order-tracker/config/order-tracker-new-common/order-products-utils';
import { Application } from '@applications/order-tracker';
import { Links, Components } from '@telstra-connect/order-tracker/activity-tracking';
import { useResize } from 'ted-ui/build/util';

const EquipmentDetails = ({ equipmentDetails }: IEquipmentDetailsProps) => {
  // Destructure business rules for displaying equipment details
  const {
    showDeliveryStatus,
    showDeliveryDate,
    showTrackConsignmentLink,
  } = productEquipmentDetailsBusinessRules(equipmentDetails);

  // Check if delivery date is empty but delivery status and tracking link are present
  const isDeliveryDateEmptyButStatusAndLinkPresent = () =>
    !showDeliveryDate() && showDeliveryStatus() && showTrackConsignmentLink();

  // Check if both delivery status and tracking link are absent
  const areBothStatusAndLinkEmpty = () =>
    !showDeliveryStatus() && !showTrackConsignmentLink();

  const [columnSize, setColumnSize] = React.useState(2);

  useResize((size) => {
    if (size === 'xs') {
      setColumnSize(1);
    } else {
      setColumnSize(2);
    }
  }, []);

  // Function to render the main equipment details section
  const renderEquipmentDetails = (): React.ReactNode => (
    <Spacing top="spacing3x">
      <ProductDetailsSection
        title={ProductSheetEquipmentDetails.Title}
        descriptionGroup={renderAllEquipmentDetailsSections()} // Render all sub-sections
      />
    </Spacing>
  );

  // Function to render all sections of equipment details
  const renderAllEquipmentDetailsSections = (): React.ReactNode => (
   <>
   <StaticDataGroup config={{
      horizontalColumn: columnSize
    }} id="equipment-details-group-data">
      {/* There is no check on consignment number field, if it is not there whole section will be hidden */}
      {renderConsignmentNumberSection()}

      {/* Render delivery date if applicable and if either status or link is not empty */}
      {(showDeliveryDate() && !areBothStatusAndLinkEmpty()) &&
        renderDeliveryDateSection()}

      {/* Render delivery status if delivery date is empty but status & link are present */}
      {isDeliveryDateEmptyButStatusAndLinkPresent() &&
        renderDeliveryStatusSection()}

      {/* Render delivery status if applicable and if delivery date is not empty */}
      {(showDeliveryStatus() && !isDeliveryDateEmptyButStatusAndLinkPresent()) &&
        renderDeliveryStatusSection()}

      {/* Render tracking link if applicable */}
      {showTrackConsignmentLink() &&
        renderTrackConsignmentLinkSection()}

      {/* If both status and link are empty, render delivery date if applicable */}
      {(areBothStatusAndLinkEmpty() && showDeliveryDate()) &&
        renderDeliveryDateSection()}
      </StaticDataGroup>
    </>
  );

  // Function to render the consignment number sub-section
  const renderConsignmentNumberSection = () => (
    <StaticData
      id="static-data-test"
      name={ProductSheetEquipmentDetails.ConsignmentNumber}
      value={equipmentDetails.consignmentNumber}
    />
  );

  // Function to render the delivery date sub-section
  const renderDeliveryDateSection = () => (
    <StaticData
      id="static-data-test"
      name={ProductSheetEquipmentDetails.EstimatedDeliveryDate}
      value={equipmentDetails.deliveryDate}
    />
  );

  // Function to render the delivery status sub-section
  const renderDeliveryStatusSection = () => (
    <StaticData
      id="static-data-test"
      name={ProductSheetEquipmentDetails.DeliveryStatus}
      value={equipmentDetails.deliveryStatus}
    />
  );

  const trackConsignmentLinkClick = (): void => {
    Application.tracker.trackLink(
      Links.productDetails.TrackConsignment,
      Components.productDetailsPage
    );
  };

  // Function to render the track consignment link
  const renderTrackConsignmentLinkSection = () => (
    <dd>
      <ActionButton
        element="Link"
        opensNewTab={true}
        label={ProductSheetEquipmentDetails.ActionButtonLabel}
        variant="LowEmphasis"
        events={{ onClick: () => trackConsignmentLinkClick() }}
        to={equipmentDetails.trackingURL}
      />
    </dd>
  );

  // Render the main component structure
  return <React.Fragment>{renderEquipmentDetails()}</React.Fragment>;
};

export default EquipmentDetails;
